import React, { useRef } from 'react'
import { Checkbox, Col, Form, Modal, Radio, Row, Typography } from 'antd'
import { Formik, FormikProps } from 'formik'
import { object, string } from 'yup'
import { useTranslation } from 'react-i18next'
import Item from 'antd/lib/list/Item'

import { Title } from 'src/components/Ant'
import { Input, InputNumber, Select } from 'src/components/Formik'
import {
  Policy,
  PolicyAction,
  PolicyOperator,
  PolicyRequestGeneralRule
} from 'src/types'

import { DaysTitleTooltipHint } from './days-title-tooltip-hint'
import * as CSS from './styles'

import {
  ButtonAddNew,
  PolicyRuleAlertIcon,
  RecordText,
  SquareIcon,
  tOperator
} from '../../utils'

interface GeneralRulesProps {
  formikProps: FormikProps<Policy>
  policyRuleIndex: number
}

export default function GeneralRules({
  formikProps,
  policyRuleIndex
}: GeneralRulesProps) {
  const { t } = useTranslation()
  const refFormik = useRef(null)
  const dataSource = []

  const { policy_rule_id } = formikProps.values.policy_rules?.[policyRuleIndex]
  if (
    formikProps.values?.policy_rules?.[policyRuleIndex]
      ?.policy_request_general_rule
  ) {
    dataSource[0] =
      formikProps.values?.policy_rules?.[
        policyRuleIndex
      ]?.policy_request_general_rule
  }

  function handleSubmit(values: PolicyRequestGeneralRule) {
    refFormik.current?.resetForm(null)
    formikProps.setFieldValue(
      `policy_rules[${policyRuleIndex}].policy_request_general_rule`,
      values
    )
  }

  const initialValues: PolicyRequestGeneralRule = {
    action: PolicyAction.ALERT,
    active: true,
    description: '',
    minimum_days_limit: false,
    operator: PolicyOperator.LESS_THAN_OR_EQUAL_TO,
    policy_request_general_rule_id: 'new',
    policy_request_rule_prefix: '',
    policy_rule_id,
    service_by_customer_supplier: false,
    value: 0
  }

  function add() {
    refFormik.current?.resetForm(initialValues)
  }

  const operators = [
    {
      label: t('common.condition.less-than'),
      value: '<'
    },
    {
      label: t('common.condition.less-than-or-equal'),
      value: '<='
    },
    {
      label: t('common.condition.equal'),
      value: '=='
    },
    {
      label: t('common.condition.greater-than'),
      value: '>'
    },
    {
      label: t('common.condition.greater-than-or-equal'),
      value: '>='
    }
  ]

  const tActive = t('common.active')
  const tAction = t('web.policies.action.title')
  const tAlert = t('web.policies.action.alert')
  const tBlock = t('web.policies.action.block')
  const tModalTitle = t('web.policies.rules.general-rules.modal-title')
  const tNo = t('common.actions.no')
  const tYes = t('common.actions.yes')

  return (
    <>
      <Title level={4} title="web.policies.rules.general-rules.title" />
      <CSS.RuleContainer>
        <CSS.List
          bordered
          dataSource={dataSource}
          header={
            <Row>
              <Col span={2}>
                <Typography.Text strong>
                  {t('web.policies.rules.prefix')}
                </Typography.Text>
              </Col>
              <Col span={6}>
                <Typography.Text strong>
                  {t('web.policies.rules.description')}
                </Typography.Text>
              </Col>
              <CSS.Col span={2}>
                <Typography.Text strong>
                  {t(
                    'web.policies.rules.general-rules.table.service-by-customer-supplier'
                  )}
                </Typography.Text>
              </CSS.Col>
              <CSS.Col span={4}>
                <Typography.Text strong>
                  {t(
                    'web.policies.rules.general-rules.table.minimum-days-limit'
                  )}
                </Typography.Text>
              </CSS.Col>
              <CSS.Col span={4}>
                <Typography.Text strong>
                  {t('common.condition.title')}
                </Typography.Text>
              </CSS.Col>
              <CSS.Col span={2}>
                <Typography.Text strong>
                  {t('web.policies.rules.general-rules.days')}
                </Typography.Text>
              </CSS.Col>
              <CSS.Col span={2}>
                <Typography.Text strong>
                  {t('web.policies.action.title')}
                </Typography.Text>
              </CSS.Col>
              <CSS.Col span={2}>
                <Typography.Text strong>{tActive}</Typography.Text>
              </CSS.Col>
            </Row>
          }
          locale={{ emptyText: <ButtonAddNew onClick={add} /> }}
          renderItem={item => (
            <Item
              key={item.policy_request_general_rule_id}
              onClick={() => {
                refFormik.current?.resetForm(item)
              }}
            >
              <Col span={2}>
                <RecordText text={item.policy_request_rule_prefix} />
              </Col>
              <Col span={6}>
                <RecordText text={item.description} />
              </Col>
              <CSS.Col span={2}>
                <SquareIcon square={item.service_by_customer_supplier} />
              </CSS.Col>
              <CSS.Col span={4}>
                <SquareIcon square={item?.minimum_days_limit} />
              </CSS.Col>
              <CSS.Col span={4}>{t(tOperator(item.operator))}</CSS.Col>
              <CSS.Col span={2}>{item.value || 0}</CSS.Col>
              <CSS.Col span={2}>
                <PolicyRuleAlertIcon
                  action={item.action}
                  alert={tAlert}
                  block={tBlock}
                />
              </CSS.Col>
              <CSS.Col onClick={event => event.stopPropagation()} span={2}>
                <Checkbox
                  defaultChecked={Boolean(item?.active)}
                  onChange={value => {
                    const { checked } = value.target
                    formikProps.setFieldValue(
                      `policy_rules[0].policy_request_general_rule.active`,
                      checked
                    )
                  }}
                />
              </CSS.Col>
            </Item>
          )}
        />
      </CSS.RuleContainer>
      <Formik
        initialValues={null}
        onSubmit={handleSubmit}
        ref={refFormik}
        render={(editLimit: FormikProps<PolicyRequestGeneralRule>) => {
          const visible = Boolean(
            editLimit?.values?.policy_request_general_rule_id
          )

          return (
            <Modal
              centered
              closable
              mask
              maskClosable
              onCancel={() => {
                refFormik.current?.resetForm(null)
              }}
              onOk={() => {
                //validate?
                refFormik.current?.handleSubmit()
              }}
              title={tModalTitle}
              visible={visible}
              width={700}
            >
              <Row gutter={24}>
                <Col span={6}>
                  <Input
                    data-test-id="policy-request-rule-prefix"
                    formikProps={editLimit}
                    label="web.policies.rules.prefix"
                    name="policy_request_rule_prefix"
                    placeholder="web.policies.rules.prefix-placeholder"
                  />
                </Col>
                <Col span={18}>
                  <Input
                    data-test-id="policy-request-rule-description"
                    formikProps={editLimit}
                    label="web.policies.rules.description"
                    name="description"
                    required={false}
                    placeholder="web.policies.rules.description-placeholder"
                  />
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={24}>
                  <Form.Item
                    colon={false}
                    label={t(
                      'web.policies.rules.general-rules.service-by-customer-supplier'
                    )}
                  >
                    <Radio.Group
                      buttonStyle="solid"
                      onChange={e => {
                        const { value } = e.target
                        editLimit.setFieldValue(
                          'service_by_customer_supplier',
                          value
                        )
                      }}
                      size="small"
                      value={editLimit.values?.service_by_customer_supplier}
                    >
                      <Radio.Button value={true}>{tYes}</Radio.Button>
                      <Radio.Button value={false}>{tNo}</Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={10}>
                  <Form.Item
                    colon={false}
                    label={t(
                      'web.policies.rules.general-rules.minimum-days-limit'
                    )}
                  >
                    <Radio.Group
                      buttonStyle="solid"
                      onChange={e => {
                        const { value } = e.target
                        editLimit.setFieldValue('minimum_days_limit', value)
                      }}
                      size="small"
                      value={editLimit.values?.minimum_days_limit}
                    >
                      <Radio.Button value={true}>{tYes}</Radio.Button>
                      <Radio.Button value={false}>{tNo}</Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col span={7}>
                  <Select
                    dataTestId="operator-list"
                    formikProps={editLimit}
                    label="common.condition.title"
                    name="operator"
                    options={operators}
                    required={false}
                  />
                </Col>
                <Col span={7}>
                  <InputNumber
                    iconInfo={<DaysTitleTooltipHint />}
                    dataTestId="policy-limit"
                    formikProps={editLimit}
                    label="web.policies.rules.general-rules.days"
                    name="value"
                    required={false}
                  />
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={6}>
                  <Form.Item colon={false} label={tAction}>
                    <Radio.Group
                      buttonStyle="solid"
                      onChange={e => {
                        let policyAction: PolicyAction = PolicyAction.ALERT
                        if (e.target.value === 'BLOCK') {
                          policyAction = PolicyAction.BLOCK
                        }

                        editLimit.setFieldValue('action', policyAction)
                      }}
                      size="small"
                      value={editLimit.values?.action}
                    >
                      <Radio.Button value="ALERT">{tAlert}</Radio.Button>
                      <Radio.Button value="BLOCK">{tBlock}</Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
            </Modal>
          )
        }}
        validationSchema={object().shape({
          policy_request_rule_prefix: string()
            .trim()
            .required('common.required'),
          operator: string().trim().required('common.required')
        })}
      />
    </>
  )
}
